/**
 * Архив — поиск накладной с фото (владелец, 22.09.2026): «найти по номеру,
 * дате или сумме — именно фото, чтобы сверить данные в системе с бумагой».
 *
 * Ищет сервер (`services/ops/archive.py`): по поставщику, номеру из склада и
 * с бумаги, тексту на снимках, дате документа и сумме. Находка открывается
 * справа той же карточкой, что и в очереди, — с фото и данными склада рядом.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { Empty, ErrorState, Loading } from '@shell/states';
import { portalApi, rub } from '../../api';
import type { ArchiveItem, ArchiveResult } from '../../api';
import { Icon } from '../../icons';
import { isoDate, when } from '../../ui';

const STATUS: Record<string, string> = {
  posted: 'проведена',
  green: 'сошлась',
  yellow: 'проверить',
  red: 'расхождение',
  waiting_draft: 'ждёт черновика',
  cancelled: 'убрана',
  checking: 'проверяется',
  posting: 'проводится',
};

function useDebounced<T>(value: T, ms = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

export function Archive({
  selectedId,
  onOpen,
  money,
}: {
  selectedId: number | null;
  onOpen: (id: number) => void;
  money: boolean;
}) {
  const [q, setQ] = useState('');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [amount, setAmount] = useState('');
  const params = useDebounced({ q: q.trim(), since, until, amount: amount.trim() });
  const asked = Boolean(params.q || params.since || params.until || params.amount);

  const query = useQuery<ArchiveResult, ApiError>({
    queryKey: ['receipts', 'archive', params],
    queryFn: () => portalApi.archive(params),
    enabled: asked,
    staleTime: 60_000,
  });

  return (
    <div className="archive">
      <label className="search">
        <Icon name="search" size={16} />
        <input
          className="search__input"
          type="search"
          placeholder="Номер, поставщик, товар с накладной"
          value={q}
          onChange={(event) => setQ(event.target.value)}
          aria-label="Что искать в архиве"
          autoFocus
        />
      </label>
      <div className="archive__filters">
        <label className="mini-field">
          <span>Дата с</span>
          <input
            className="input"
            type="date"
            value={since}
            onChange={(event) => setSince(event.target.value)}
            aria-label="Дата накладной с"
          />
        </label>
        <label className="mini-field">
          <span>по</span>
          <input
            className="input"
            type="date"
            value={until}
            onChange={(event) => setUntil(event.target.value)}
            aria-label="Дата накладной по"
          />
        </label>
        {money && (
          <label className="mini-field">
            <span>Сумма</span>
            <input
              className="input input--num"
              inputMode="decimal"
              placeholder="8 910,00"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              aria-label="Сумма накладной"
            />
          </label>
        )}
      </div>

      <div className="queue__list">
        {!asked ? (
          <Empty
            title="Поиск по фото накладных"
            text="Номер, поставщик, дата, сумма или слово с самой бумаги — например, название товара. Фото хранятся пять лет."
          />
        ) : query.isPending ? (
          <Loading />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : query.data.items.length === 0 ? (
          <Empty title="Ничего не нашлось" text="Попробуйте часть номера или другое слово." />
        ) : (
          <>
            <ul className="queue-items">
              {query.data.items.map((item) => (
                <ArchiveRow
                  key={item.receipt_id}
                  item={item}
                  selected={item.receipt_id === selectedId}
                  onOpen={() => onOpen(item.receipt_id)}
                />
              ))}
            </ul>
            {query.data.more && (
              <p className="faint archive__more">Показаны первые 50 — уточните запрос.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ArchiveRow({
  item,
  selected,
  onOpen,
}: {
  item: ArchiveItem;
  selected: boolean;
  onOpen: () => void;
}) {
  const number = item.invoice_number ?? item.paper_numbers[0] ?? 'без номера';
  const date = item.invoice_date ? isoDate(item.invoice_date) : item.paper_dates[0] ?? null;
  return (
    <li>
      <button
        type="button"
        className={`queue-item${selected ? ' queue-item--selected' : ''}`}
        onClick={onOpen}
        aria-current={selected ? 'true' : undefined}
      >
        <span className="queue-item__top">
          <span className="queue-item__supplier">{item.supplier || 'Поставщик не узнан'}</span>
          <span className="queue-item__time">{STATUS[item.status] ?? item.status}</span>
        </span>
        <span className="queue-item__title">
          № {number}
          {date ? ` · ${date}` : ''}
          {item.invoice_total ? ` · ${rub(item.invoice_total)}` : ''}
        </span>
        <span className="queue-item__reason">
          {item.pages} фото · сдана {when(item.received_at ?? '')}
          {item.why.length > 0 ? ` · совпало: ${item.why.join(', ')}` : ''}
        </span>
      </button>
    </li>
  );
}
